import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import { authApi } from '@/api/clients/authApi'
import { economicsApi } from '@/api/clients/economicsApi'
import { scoringConfigApi } from '@/api/clients/scoringConfigApi'
import { orgComplianceApi } from '@/api/clients/orgComplianceApi'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { StandardsPicker } from '@/components/common/StandardsPicker'
import type { OrgDefaults, ScoringConfig } from '@/api/types'
import { PageHeader, Card, Button, Field, TextInput, SelectInput, Stat } from '@/components/common/ui'
import { Spinner } from '@/components/common/State'
import { MoneyValue } from '@/components/common/MoneyValue'

type Tab = 'general' | 'scoring' | 'compliance' | 'economics' | 'usage'

export default function Settings() {
  const [tab, setTab] = useState<Tab>('general')
  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'general', label: 'General', icon: 'settings' },
    { key: 'scoring', label: 'Risk scoring', icon: 'tune' },
    { key: 'compliance', label: 'Compliance standards', icon: 'rule' },
    { key: 'economics', label: 'Economics defaults', icon: 'payments' },
    { key: 'usage', label: 'AI usage', icon: 'insights' },
  ]
  return (
    <div>
      <PageHeader title="Settings" subtitle="Your account, appearance, and org $-model defaults" />
      <div className="flex gap-1 border-b border-border-light dark:border-border-dark mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            <span className="material-icons text-base">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'general' && <GeneralTab />}
      {tab === 'scoring' && <RiskScoringTab />}
      {tab === 'compliance' && <ComplianceTab />}
      {tab === 'economics' && <EconomicsDefaultsTab />}
      {tab === 'usage' && <UsageTab />}
    </div>
  )
}


const SETTINGS_CARD =
  'bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl border border-border-light dark:border-border-dark p-6'

function GeneralTab() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const { currentOrgId, orgName } = useOrgStore()
  const userEmail = useAuthStore((s) => s.userEmail)
  const role = useAuthStore((s) => s.role)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const clearSession = useAuthStore((s) => s.clearSession)
  const qc = useQueryClient()

  // Org region / jurisdiction — a platform-wide attribute (drives region-specific
  // features like the executive report's CSA guidance). Stored with the org config;
  // save preserves the compliance standards it shares a record with.
  const cfg = useQuery({
    queryKey: ['org-compliance', currentOrgId],
    queryFn: () => orgComplianceApi.get(currentOrgId),
    enabled: !!currentOrgId,
  })
  const [region, setRegion] = useState<string | null>(null)
  const regionValue = region ?? cfg.data?.region ?? ''
  const saveRegion = useMutation({
    mutationFn: () => orgComplianceApi.set(currentOrgId, cfg.data?.standards ?? [], regionValue),
    onSuccess: (d) => {
      setRegion(d.region ?? '')
      qc.invalidateQueries({ queryKey: ['org-compliance', currentOrgId] })
      qc.invalidateQueries({ queryKey: ['exec-report', currentOrgId] })
    },
  })

  const handleLogout = async () => {
    if (refreshToken) await authApi.logout(refreshToken).catch(() => {/* best-effort */})
    clearSession()
    navigate('/login', { replace: true })
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Account */}
      <div className={SETTINGS_CARD}>
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">Account</h2>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            {userEmail && (
              <p className="text-sm font-medium text-text-light dark:text-text-dark">{userEmail}</p>
            )}
            {role && <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">{role}</p>}
            <p className="text-xs text-subtle-light dark:text-subtle-dark">
              Organisation:{' '}
              <span className="font-medium text-text-light dark:text-text-dark">{orgName}</span>
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark">
              Org ID: <span className="font-mono">{currentOrgId}</span>
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="shrink-0 flex items-center gap-2 px-4 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-error"
          >
            <span className="material-icons text-base">logout</span>
            Sign out
          </button>
        </div>
      </div>

      {/* Appearance */}
      <div className={SETTINGS_CARD}>
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">Appearance</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-text-light dark:text-text-dark">Theme</p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
              Currently: <strong>{theme}</strong> mode
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
          >
            <span className="material-icons text-base">{theme === 'light' ? 'dark_mode' : 'light_mode'}</span>
            Switch to {theme === 'light' ? 'Dark' : 'Light'} Mode
          </button>
        </div>
      </div>

      {/* Region / jurisdiction */}
      <div className={SETTINGS_CARD}>
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-1">
          Region / Jurisdiction
        </h2>
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">
          Platform-wide — drives region-specific features (e.g. the executive report's CSA guidance).
          Auto infers from the frameworks in scope.
        </p>
        <div className="flex items-center gap-3">
          <select
            value={regionValue}
            onChange={(e) => setRegion(e.target.value)}
            disabled={cfg.isLoading}
            className="w-full max-w-xs px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {REGION_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            icon="save"
            disabled={saveRegion.isPending || cfg.isLoading}
            onClick={() => saveRegion.mutate()}
          >
            {saveRegion.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {saveRegion.error && (
          <p className="mt-2 text-sm text-error">{(saveRegion.error as Error).message}</p>
        )}
      </div>

      {/* About */}
      <div className={SETTINGS_CARD}>
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">About</h2>
        <div className="space-y-2 text-sm text-subtle-light dark:text-subtle-dark">
          <p>
            <strong className="text-text-light dark:text-text-dark">Quantizant Risk</strong> — v0.1.0
          </p>
          <p>Frontend for the Quantizant PQC Transition Platform risk module.</p>
          <p>
            Scores post-quantum risk across the crypto estate — bands, HNDL windows, $-at-risk, and
            migration posture — and drives alerts, compliance, and remediation ROI.
          </p>
        </div>
      </div>
    </div>
  )
}

function RiskScoringTab() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()
  const cfg = useQuery({
    queryKey: ['scoring-config', orgId],
    queryFn: () => scoringConfigApi.get(orgId),
    enabled: !!orgId,
  })
  const [form, setForm] = useState<ScoringConfig | null>(null)
  useEffect(() => {
    if (cfg.data && !form) setForm(cfg.data)
  }, [cfg.data, form])

  const save = useMutation({
    mutationFn: () => scoringConfigApi.set(orgId, form as ScoringConfig),
    onSuccess: (d) => {
      setForm(d)
      qc.invalidateQueries({ queryKey: ['scoring-config', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
    },
  })

  if (cfg.isLoading || !form) return <Spinner />
  const patch = (p: Partial<ScoringConfig>) => setForm((f) => ({ ...(f as ScoringConfig), ...p }))
  const numField = (label: string, key: keyof ScoringConfig, step = 'any') => (
    <Field label={label}>
      <TextInput
        type="number"
        step={step}
        value={(form[key] as number) ?? ''}
        onChange={(e) => patch({ [key]: Number(e.target.value) } as Partial<ScoringConfig>)}
      />
    </Field>
  )

  return (
    <div className="space-y-4">
      <Card title="Band cutoffs (0–100 score → band)">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          A score ≥ cutoff maps to that band. Must be strictly decreasing.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {numField('Critical ≥', 'band_critical')}
          {numField('High ≥', 'band_high')}
          {numField('Medium ≥', 'band_medium')}
          {numField('Low ≥', 'band_low')}
        </div>
      </Card>

      <Card title="Business-factor weights">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          businessFactor = floor + data·dataSensitivity + crit·criticality + reg·regulatoryScope
          (scales the per-asset score).
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {numField('Floor', 'bf_floor')}
          {numField('Data sensitivity', 'bf_data_weight')}
          {numField('Criticality', 'bf_criticality_weight')}
          {numField('Regulatory scope', 'bf_regulatory_weight')}
        </div>
      </Card>

      <Card title="Physics parameters (HNDL / Mosca window)">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          mosca_window = (time to quantum year − now) − (shelf life + migration). Smaller window →
          higher urgency. Shelf/migration are defaults; per-asset values override them.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {numField('Time to quantum year', 'time_to_quantum_year', '1')}
          {numField('Default shelf life (years)', 'default_shelf_life', '1')}
          {numField('Default migration (years)', 'default_migration', '1')}
        </div>
      </Card>

      <Card title="Org risk-score band weights">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          The dashboard gauge = severity-weighted mean of the band distribution using these.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {numField('Critical', 'score_weight_critical', '1')}
          {numField('High', 'score_weight_high', '1')}
          {numField('Medium', 'score_weight_medium', '1')}
          {numField('Low', 'score_weight_low', '1')}
          {numField('Info', 'score_weight_info', '1')}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save scoring config'}
          </Button>
          {save.error && <p className="text-sm text-error">{(save.error as Error).message}</p>}
        </div>
        <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
          Saving re-scores all assets, so bands + the gauge update immediately.
        </p>
      </Card>
    </div>
  )
}

const REGION_OPTIONS = [
  { value: '', label: 'Auto (derive from frameworks)' },
  { value: 'SG', label: 'Singapore' },
]

function ComplianceTab() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()
  const cfg = useQuery({
    queryKey: ['org-compliance', orgId],
    queryFn: () => orgComplianceApi.get(orgId),
    enabled: !!orgId,
  })
  const [selected, setSelected] = useState<string[] | null>(null)
  const value = selected ?? cfg.data?.standards ?? []

  const save = useMutation({
    // Preserve the org region (shares the record) so saving standards doesn't clear it.
    mutationFn: () => orgComplianceApi.set(orgId, value, cfg.data?.region ?? ''),
    onSuccess: (d) => {
      setSelected(d.standards)
      qc.invalidateQueries({ queryKey: ['org-compliance', orgId] })
      qc.invalidateQueries({ queryKey: ['crosswalk', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
      qc.invalidateQueries({ queryKey: ['assets', orgId] })
    },
  })

  if (cfg.isLoading) return <Spinner />

  return (
    <div className="space-y-4">
      <Card title="Org default compliance standards">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          Applied to every inventory that has no frameworks of its own. An inventory with its own
          frameworks overrides these. Drives migration deadlines + the dashboard compliance posture.
        </p>
        <StandardsPicker orgId={orgId} value={value} onChange={setSelected} />
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save standards'}
          </Button>
          {save.error && <p className="text-sm text-error">{(save.error as Error).message}</p>}
        </div>
        <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
          Saving re-scores all assets so deadlines + posture update immediately.
        </p>
      </Card>
    </div>
  )
}

// The 8 composition weights map to these $-at-risk components, in order (see
// internal/economics: weightedOutage w0–3, weightedData w4–6, w7 regulatory).
const WEIGHT_COMPONENTS = [
  'Revenue at risk',
  'Downtime exposure',
  'Transaction exposure',
  'SLA exposure',
  'Breach cost',
  'HNDL data exposure',
  'RPO data loss',
  'Regulatory exposure',
]
const COMPOSITION_STRATEGIES = ['weighted_sum', 'worst_of_cluster', 'worst_of_all']

function EconomicsDefaultsTab() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()
  const defaults = useQuery({
    queryKey: ['econ-defaults', orgId],
    queryFn: () => economicsApi.getDefaults(orgId),
    enabled: !!orgId,
  })
  const packs = useQuery({
    queryKey: ['weight-packs', orgId],
    queryFn: () => economicsApi.weightPacks(orgId),
    enabled: !!orgId,
  })

  const [form, setForm] = useState<OrgDefaults | null>(null)
  useEffect(() => {
    if (defaults.data && !form) setForm(defaults.data)
  }, [defaults.data, form])

  const save = useMutation({
    mutationFn: () => economicsApi.setDefaults(orgId, form as OrgDefaults),
    onSuccess: (d) => {
      setForm(d)
      qc.invalidateQueries({ queryKey: ['econ-defaults', orgId] })
      qc.invalidateQueries({ queryKey: ['summary', orgId] })
      // Org $-model defaults change every inventory's exposure → refresh $ views.
      qc.invalidateQueries({ queryKey: ['inventory-impact', orgId] })
      qc.invalidateQueries({ queryKey: ['money-trend', orgId] })
      qc.invalidateQueries({ queryKey: ['money-trends', orgId] })
      qc.invalidateQueries({ queryKey: ['econ-last-changed', orgId] })
    },
  })

  if (defaults.isLoading || !form) return <Spinner />

  const patch = (p: Partial<OrgDefaults>) => setForm((f) => ({ ...(f as OrgDefaults), ...p }))
  const weights = form.weights ?? new Array(8).fill(0)
  const setWeight = (i: number, v: number) =>
    patch({ weights: weights.map((w, j) => (j === i ? v : w)) })

  // Applying a named sector pack replaces the weight vector from the pack preset.
  const applyPack = (id: string) => {
    const pk = packs.data?.find((p) => p.id === id)
    patch({ weight_pack: id, ...(pk ? { weights: pk.weights } : {}) })
  }

  const num = (v: unknown) => (typeof v === 'number' ? v : '')

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Org $ defaults">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Base currency">
              <TextInput
                value={(form.base_currency as string) ?? ''}
                onChange={(e) => patch({ base_currency: e.target.value })}
              />
              <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark">
                Display label only — enter all economics in this currency. Changing it does not
                convert existing values (no FX conversion).
              </p>
            </Field>
            <Field label="Cost / record">
              <TextInput type="number" value={num(form.cost_per_record)} onChange={(e) => patch({ cost_per_record: Number(e.target.value) })} />
            </Field>
            <Field label="FTE daily rate">
              <TextInput type="number" value={num(form.fte_daily_rate)} onChange={(e) => patch({ fte_daily_rate: Number(e.target.value) })} />
            </Field>
            <Field label="Discount rate (e.g. 0.03)">
              <TextInput type="number" value={num(form.discount_rate)} onChange={(e) => patch({ discount_rate: Number(e.target.value) })} />
            </Field>
            <Field label="Horizon (years)">
              <TextInput type="number" value={num(form.horizon_years)} onChange={(e) => patch({ horizon_years: Number(e.target.value) })} />
            </Field>
          </div>
        </Card>

        <Card title="Composition">
          <Field label="Strategy">
            <SelectInput
              value={(form.composition_strategy as string) ?? 'weighted_sum'}
              onChange={(e) => patch({ composition_strategy: e.target.value })}
            >
              {COMPOSITION_STRATEGIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Sector weight pack (preset)">
            <SelectInput value={(form.weight_pack as string) ?? ''} onChange={(e) => applyPack(e.target.value)}>
              <option value="">— custom —</option>
              {packs.data?.map((p) => (
                <option key={p.id} value={p.id}>{p.id}</option>
              ))}
            </SelectInput>
          </Field>
          <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
            Strategy = how components combine ($-at-risk headline uses this). Picking a pack
            replaces the weights below; edit them for a custom mix.
          </p>
        </Card>
      </div>

      <Card title="Component weights">
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          Per-component multiplier in the weighted-sum $-at-risk composition.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {WEIGHT_COMPONENTS.map((label, i) => (
            <Field key={label} label={label}>
              <TextInput
                type="number"
                value={num(weights[i])}
                onChange={(e) => setWeight(i, Number(e.target.value))}
              />
            </Field>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save economics defaults'}
          </Button>
          {save.error && <p className="text-sm text-error">{(save.error as Error).message}</p>}
        </div>
      </Card>
    </div>
  )
}

function UsageTab() {
  const { currentOrgId: orgId } = useOrgStore()
  const metrics = useQuery({
    queryKey: ['metrics', orgId],
    queryFn: () => analyticsApi.metrics(orgId),
    enabled: !!orgId,
  })
  if (metrics.isLoading) return <Spinner />
  const m = metrics.data
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Stat label="Assets" value={m?.asset_count?.toLocaleString() ?? '—'} />
      <Stat
        label="AI cost (month)"
        value={<MoneyValue value={m?.ai_cost_usd_month ?? 0} />}
      />
      <Stat label="AI tokens (month)" value={(m?.ai_tokens_month ?? 0).toLocaleString()} />
    </div>
  )
}
